import { useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import InventoryReminderSettings from './components/InventoryReminderSettings';
import SettingsService from './services/settingsService';
import { InventoryReminderScheduleConfig } from '../../types/inventoryReminder';
import { useGlobalStore } from '../../application/store/store';

const OrganizationSettingsPage = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { setMessage, startLoading, stopLoading } = useGlobalStore();

    const [isLoading, setIsLoading] = useState(true);
    const [inventoryReminderConfig, setInventoryReminderConfig] =
        useState<InventoryReminderScheduleConfig | null>(null);

    const settingsService = new SettingsService();

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                setIsLoading(true);
                startLoading();
                const config =
                    await settingsService.getInventoryReminderSchedule();
                setInventoryReminderConfig(config);
            } catch (error) {
                console.error('Error fetching settings:', error);
                setMessage(t('settings.errors.loadFailed'), 'error', 5000);
            } finally {
                setIsLoading(false);
                stopLoading();
            }
        };

        fetchSettings();
    }, []);

    const handleConfigChange = (
        config: InventoryReminderScheduleConfig | null
    ) => {
        setInventoryReminderConfig(config);
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '50vh',
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Button
                            startIcon={<ArrowBackIcon />}
                            onClick={() => navigate(-1)}
                            variant="outlined"
                            sx={{
                                borderColor: 'gray.300',
                                color: 'gray.700',
                                '&:hover': {
                                    borderColor: 'gray.400',
                                    backgroundColor: 'gray.50',
                                },
                            }}
                        >
                            {t('common.back')}
                        </Button>
                        <Typography
                            variant="h5"
                            className="font-semibold text-gray-900"
                        >
                            {t('settings.organizationSettings.title')}
                        </Typography>
                        <div>
                            {/* Spara-knapp dold - funktion under utveckling */}
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
                <Alert severity="warning" sx={{ mb: 3 }}>
                    {t('settings.underDevelopment')}
                    Vi behöver först ändra så att featuresAndConfigurations
                    sparas per organisation (nu sparas det per tenant).
                </Alert>

                <InventoryReminderSettings
                    config={inventoryReminderConfig}
                    onChange={handleConfigChange}
                    disabled={true}
                />
            </div>
        </div>
    );
};

export default OrganizationSettingsPage;
